import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import WarningOutlined from '@ant-design/icons/WarningOutlined';
import { useVideoState } from '@msi-alta/opvideo-state-machine';
import { Layers, SoundEffects, WebAudio } from 'utils/audio';
import { getOpParams } from 'utils/getOpParams';
import { useFeatureFlag } from 'utils/customHooks/useFeatureFlag';
import { Popup } from 'components/Popup';
import { PhoneFilledSvg } from 'components/svgs/PhoneFilledSvg/PhoneFilledSvg';
import { OpButton } from 'new-components/DLS/OpButton/OpButton';
import { callStates, connectionStates } from '../constants';

const styles = {
  wrapper: { paddingTop: 9 },
  button: { transform: 'rotate(135deg)' },
};

const CALL_ICONS = {
  ERROR: <WarningOutlined />,
  START_CALL: <PhoneFilledSvg />,
  END_CALL: <PhoneFilledSvg style={{ transform: 'rotate(135deg)' }} />,
  NO_ICON: null,
};
// TODO: Update opvideo-state-machine so that states can be imported when isolatedModules is enabled
const VideoStates = {
  CONNECTING: 'CONNECTING',
  CONNECTED: 'CONNECTED',
  PREVIEW: 'PREVIEW',
  SURVEILLANCE: 'SURVEILLANCE',
  CALL: 'CALL',
  FAILED: 'FAILED',
  RECONNECTING: 'RECONNECTING',
  DISCONNECTING: 'DISCONNECTING',
  DISCONNECTED: 'DISCONNECTED',
};

const getIconKey = (icon: any) => {
  return (
    Object.keys(CALL_ICONS).find(
      (key) => CALL_ICONS[key as keyof typeof CALL_ICONS] === icon,
    ) || null
  );
};

/*
  ConnectionState models the actual webRTC connection state of an ongoing call
  CallState determines if the call is in "Preview mode" (no mic) or "Call mode" (mic enabled)
*/

interface WebRtcButtonProps {
  onClick(): void;
  connectionState: keyof typeof connectionStates;
  callState: keyof typeof callStates | null;
  micPermission: string | null;
}

const WebRtcButton = memo(
  ({
    onClick,
    connectionState,
    callState = null,
    micPermission,
  }: WebRtcButtonProps) => {
    const { t } = useTranslation();

    const { orgId } = getOpParams();
    const { value: isVideoStateMachineEnabled } = useFeatureFlag(
      'IS_VIDEO_STATE_MACHINE_ENABLED',
      orgId,
    );

    const { videoState } = useVideoState();
    const { value: currentVideoState } = videoState;

    const isDangerStatus = useMemo(() => {
      return isVideoStateMachineEnabled
        ? currentVideoState === VideoStates.FAILED ||
            currentVideoState === VideoStates.CALL
        : connectionState === connectionStates.CONNECTION_ERROR ||
            (connectionState === connectionStates.CONNECTED &&
              callState === callStates.CALL);
    }, [
      isVideoStateMachineEnabled,
      currentVideoState,
      connectionState,
      callState,
    ]);

    const icon = useMemo(() => {
      if (isVideoStateMachineEnabled) {
        switch (currentVideoState) {
          case VideoStates.PREVIEW:
          case VideoStates.SURVEILLANCE:
            if (micPermission === 'denied') {
              return CALL_ICONS.ERROR;
            }
            return CALL_ICONS.START_CALL;
          case VideoStates.CALL:
            return CALL_ICONS.END_CALL;
          case VideoStates.CONNECTING:
          case VideoStates.DISCONNECTED:
          case VideoStates.RECONNECTING:
            return CALL_ICONS.NO_ICON;
          case VideoStates.FAILED:
            return CALL_ICONS.ERROR;
          default:
            return CALL_ICONS.ERROR;
        }
      }

      switch (connectionState) {
        case connectionStates.CONNECTED:
          if (callState === callStates.PREVIEW) {
            if (micPermission === 'denied') {
              return CALL_ICONS.ERROR;
            }
            return CALL_ICONS.START_CALL;
          }
          if (callState === callStates.CALL) {
            return CALL_ICONS.END_CALL;
          }
          break;
        case connectionStates.CONNECTING:
        case connectionStates.DISCONNECTED:
        case connectionStates.SHOULD_RECONNECT:
          return CALL_ICONS.NO_ICON;
        case connectionStates.CONNECTION_ERROR:
          return CALL_ICONS.ERROR;
        default:
          return CALL_ICONS.ERROR;
      }
    }, [
      connectionState,
      isVideoStateMachineEnabled,
      callState,
      micPermission,
      currentVideoState,
    ]);

    const popupContent = useMemo(() => {
      if (isVideoStateMachineEnabled) {
        switch (currentVideoState) {
          case VideoStates.PREVIEW:
          case VideoStates.SURVEILLANCE:
            if (micPermission === 'denied') {
              return t('Browser microphone permission denied');
            }

            return t('Start call');
          case VideoStates.CALL:
            return t('End call');
          case VideoStates.CONNECTING:
          case VideoStates.DISCONNECTED:
          case VideoStates.RECONNECTING:
            return t('Loading...');
          case VideoStates.FAILED:
            return t('Connection Error');
          default:
            return t('Unknown state');
        }
      }

      switch (connectionState) {
        case connectionStates.CONNECTED:
          if (callState === callStates.PREVIEW) {
            if (micPermission === 'denied') {
              return t('Browser microphone permission denied');
            }

            return t('Start call');
          }
          if (callState === callStates.CALL) {
            return t('End call');
          }
          break;
        case connectionStates.CONNECTING:
        case connectionStates.DISCONNECTED:
        case connectionStates.SHOULD_RECONNECT:
          return t('Loading...');
        case connectionStates.CONNECTION_ERROR:
          return t('Connection Error');
        default:
          return t('Unknown state');
      }
    }, [
      isVideoStateMachineEnabled,
      connectionState,
      currentVideoState,
      micPermission,
      t,
      callState,
    ]);

    return (
      <Popup
        className="web-rtc-button"
        trigger={
          <div
            style={styles.wrapper}
            className="control-button op-cursor-pointer op-flex-direction-column op-align-items-center"
          >
            <OpButton
              loading={
                isVideoStateMachineEnabled
                  ? [VideoStates.CONNECTING, VideoStates.DISCONNECTED].includes(
                      currentVideoState,
                    )
                  : connectionState === connectionStates.CONNECTING ||
                    connectionState === connectionStates.DISCONNECTED
              }
              data-testid={`web-rtc-button-${getIconKey(icon)}`}
              shape="circle"
              size="large"
              type="primary"
              danger={isDangerStatus}
              icon={icon}
              onClick={() => {
                if (micPermission === 'denied') {
                  // todo
                } else {
                  onClick();
                  WebAudio.stop(
                    SoundEffects.INTERCOM_NOTIFICATION,
                    Layers.INTERCOM_NOTIFICATIONS,
                  );
                }
              }}
              {...((isVideoStateMachineEnabled
                ? currentVideoState === VideoStates.CALL
                : connectionState === connectionStates.CONNECTED &&
                  callState === callStates.CALL) && {
                style: styles.button,
              })}
            />
          </div>
        }
        content={popupContent}
        position="bottom center"
      />
    );
  },
);

export default WebRtcButton;
